import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel, Container } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, CTA, Image } from '../../components';
import { getAllProductRatings } from '../../actions/ratings-reviews-actions';
import { useGlobalConfig } from '../../hooks/use-global-config';
import { isValidArray } from '../../components/utilities/validations';

import StarRatings from '../../components/common/star-ratings';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';

import styles from './index.module.scss';

const RatingsAndReviews = (props) => {
	const { ratings_explore } = useGlobalConfig();
	const pageData = props.data.contentstackRatingsAndReviews;
	const dispatch = useDispatch();
	const reviews = useSelector((state) => state.reviews);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		dispatch(getAllProductRatings());

		// OnLoad reviews.Reviews exists, so once that is updated with dispatch - set loading to false
		if (loading && !reviews.Reviews) {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [reviews.Reviews]);

	return (
		<Layout
			id='template-RatingsAndReviews'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && <HeroSimple data={pageData.hero} />}

			{pageData.top_content && (
				<div className={styles.ratingsIntro}>
					<Container width={1200}>
						<div
							dangerouslySetInnerHTML={{
								__html: pageData.top_content,
							}}
						/>
					</Container>
				</div>
			)}

			{isValidArray(pageData.ratings_by_product) &&
				pageData.ratings_by_product.map((row, index) => {
					// Check if products array is empty, then return null so title doesn't show
					if (!isValidArray(row.products)) return null;

					return (
						<div
							key={index}
							className={`${styles.sectionWrapper} ${styles.ratingsWrapper}`}
						>
							<Container className={styles.ratingsContainer}>
								{row.heading && <h3 className='s2-h3__alt'>{row.heading}</h3>}

								{isValidArray(row.products) && (
									<Carousel
										className='py-2.5'
										arrowsClassName='[&_button]:z-4'
										slidesToShow={{
											xs: 1,
											sm: 2,
											lg: 3,
											xxl: 4,
										}}
										slidePadding={20}
										showArrows
									>
										{row.products.map((prod, index) => (
											<React.Fragment key={index}>
												<div className={styles.ratingsCard}>
													{isValidArray(prod.image) && (
														<Image
															data={prod.image[0]}
															className={styles.ratingsCard_Image}
														/>
													)}

													{isValidArray(prod.ctas) && (
														<CTA
															data={prod.ctas[0]}
															className={styles.ratingsCard_Link}
															renderAs='link'
														/>
													)}

													{!loading && reviews && prod.ratings?.[0]?.ratings_code && reviews[prod.ratings[0].ratings_code]?.TotalReviews && (
														<StarRatings
															totalReviews={reviews[prod.ratings[0].ratings_code].TotalReviews}
															overallRating={reviews[prod.ratings[0].ratings_code].OverallSatisfactionRating}
															className={styles.ratingsCard_Stars}
															hideRating={true}
														/>
													)}

													{prod.product_line && (
														<div
															className={styles.ratingsCard_Product}
															dangerouslySetInnerHTML={{
																__html: prod.product_line,
															}}
														/>
													)}
												</div>
											</React.Fragment>
										))}
									</Carousel>
								)}
							</Container>

							{isValidArray(row.ctas) && (
								<div className={styles.ratingsWrapper_Button}>
									<Container fluid>
										<CTA data={row.ctas[0]} />
									</Container>
								</div>
							)}
						</div>
					);
				})}

			{pageData.ratings_explore_cards && isValidArray(ratings_explore.card) && <CardSection data={ratings_explore} />}

			{pageData.cards && <CardSection data={pageData.cards} />}
		</Layout>
	);
};

RatingsAndReviews.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default RatingsAndReviews;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackRatingsAndReviews.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackRatingsAndReviews(id: { eq: $uid }) {
			url

			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}

			hero {
				image {
					...Image
					image {
						gatsbyImageData(width: 1500, quality: 90)
					}
				}
				body
			}

			top_content

			ratings_by_product {
				banner
				heading
				products {
					product_line
					image {
						...Image
						image {
							gatsbyImageData(height: 400, quality: 90)
						}
					}
					ctas {
						...CTA
					}
					ratings {
						ratings_code
					}
				}
				ctas {
					...CTA
				}
			}
			cards {
				...CardSection
			}
			ratings_explore_cards
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
